import {
  ActiveFiltersBar,
  RemovableFilterBadge,
} from "@/components/dashboard/active-filters-bar";
import { AmsFilter } from "@/components/dashboard/ams-filter";
import { JurisdictionFilter } from "@/components/dashboard/jurisdiction-filter";
import { LicenseTypeFilter } from "@/components/dashboard/license-type-filter";
import { RegistrantFilter } from "@/components/dashboard/registrant-filter";
import {
  buildRegistrantColumnFilterBadges,
  removeColumnFilterBadge,
} from "@/components/non_registrant/dashboard-column-filter-utils";
import type { RegistrantOption } from "@/hooks/use-registrant-dashboard-data";
import { cn } from "@/lib/utils";
import type { JurisdictionOption, LicenseTypeOption } from "@/types";

interface DashboardFiltersProps {
  // Jurisdiction filter props
  selectedJurisdictions?: string[];
  onSelectedJurisdictionsChange?: (jurisdictions: string[]) => void;
  jurisdictionOptions?: JurisdictionOption[];
  showJurisdictionFilter?: boolean;
  jurisdictionOptionsCriteria?: string;

  // License type filter props
  selectedLicenseTypes?: string[];
  onSelectedLicenseTypesChange?: (licenseTypes: string[]) => void;
  licenseTypeOptions?: LicenseTypeOption[];
  showLicenseTypeFilter?: boolean;

  /** Clears the shared region selection. The region dropdown is a column filter. */
  onSelectedRegionsChange?: (regions: string[]) => void;

  // Registrant filter (company with >1 user)
  hasMultipleRegistrantAccounts?: boolean;
  registrantOptions?: RegistrantOption[];
  selectedRegistrantAccountId?: string | null;
  onSelectedRegistrantAccountIdChange?: (id: string | null) => void;
  registrantOptionsCriteria?: string;

  // Column filters (e.g. registrant dashboard table column filters)
  columnFilters?: Record<string, string[]>;
  onColumnFiltersChange?: (filters: Record<string, string[]>) => void;

  /** Registrant dashboard: "Balance due" tile narrowed list (cleared with Clear all). */
  registrantBalanceDueCohortActive?: boolean;
  onClearRegistrantBalanceDueCohort?: () => void;
  /** Registrant dashboard: "Bulk Payment" tile narrowed All Active list */
  registrantBulkPaymentPayLaterCohortActive?: boolean;
  onClearRegistrantBulkPaymentPayLaterCohort?: () => void;

  selectedAms?: string | null;
  onSelectedAmsChange?: (ams: string | null) => void;
  amsOptions?: Array<{ value: string; label: string }>;

  /** Registrant: horizontal filters with wrap; default keeps stacked-to-row at `lg` for other portals. */
  filterDropdownsLayout?: "default" | "wrap-row";

  /** `single-line` keeps count, chips, and Clear all on one row in narrow columns. */
  activeFiltersLayout?: "wrap" | "single-line";
}

export function DashboardFilters({
  selectedJurisdictions = [],
  onSelectedJurisdictionsChange,
  jurisdictionOptions,
  showJurisdictionFilter = false,
  jurisdictionOptionsCriteria,
  selectedLicenseTypes = [],
  onSelectedLicenseTypesChange,
  licenseTypeOptions,
  showLicenseTypeFilter = false,
  onSelectedRegionsChange,
  hasMultipleRegistrantAccounts = false,
  registrantOptions = [],
  selectedRegistrantAccountId = null,
  onSelectedRegistrantAccountIdChange,
  registrantOptionsCriteria,
  columnFilters = {},
  onColumnFiltersChange,
  registrantBalanceDueCohortActive = false,
  onClearRegistrantBalanceDueCohort,
  registrantBulkPaymentPayLaterCohortActive = false,
  onClearRegistrantBulkPaymentPayLaterCohort,
  selectedAms = null,
  onSelectedAmsChange,
  amsOptions = [],
  filterDropdownsLayout = "default",
  activeFiltersLayout = "wrap",
}: DashboardFiltersProps) {
  const hasFilterDropdowns =
    showJurisdictionFilter ||
    showLicenseTypeFilter ||
    (hasMultipleRegistrantAccounts && registrantOptions.length > 0) ||
    (amsOptions.length > 0 && onSelectedAmsChange);
  if (
    !hasFilterDropdowns &&
    !registrantBalanceDueCohortActive &&
    !registrantBulkPaymentPayLaterCohortActive &&
    !selectedAms
  ) {
    return null;
  }

  // Combine all active filters
  const activeFilters = [
    // Jurisdiction filters
    ...selectedJurisdictions.map((value) => {
      const jurisdiction = jurisdictionOptions?.find((j) => j.value === value);
      return {
        type: "jurisdiction",
        value,
        label: jurisdiction?.label || value,
        onRemove: () => {
          if (onSelectedJurisdictionsChange) {
            onSelectedJurisdictionsChange(
              selectedJurisdictions.filter((item) => item !== value),
            );
          }
        },
      };
    }),
    // License type filters
    ...selectedLicenseTypes.map((value) => {
      const licenseType = licenseTypeOptions?.find((lt) => lt.value === value);
      return {
        type: "licenseType",
        value,
        label: licenseType?.label || value,
        onRemove: () => {
          if (onSelectedLicenseTypesChange) {
            onSelectedLicenseTypesChange(
              selectedLicenseTypes.filter((item) => item !== value),
            );
          }
        },
      };
    }),
    // Registrant filter
    ...(selectedRegistrantAccountId && onSelectedRegistrantAccountIdChange
      ? [
          {
            type: "registrant" as const,
            value: selectedRegistrantAccountId,
            label:
              registrantOptions.find(
                (r) => r.value === selectedRegistrantAccountId,
              )?.label ?? selectedRegistrantAccountId,
            onRemove: () => onSelectedRegistrantAccountIdChange(null),
          },
        ]
      : []),
    ...(selectedAms && onSelectedAmsChange
      ? [
          {
            type: "ams" as const,
            value: selectedAms,
            label: selectedAms,
            onRemove: () => onSelectedAmsChange(null),
          },
        ]
      : []),
    ...(registrantBalanceDueCohortActive && onClearRegistrantBalanceDueCohort
      ? [
          {
            type: "balanceDueCohort" as const,
            value: "balance-due-cohort",
            label: "Balance due",
            onRemove: onClearRegistrantBalanceDueCohort,
          },
        ]
      : []),
    ...(registrantBulkPaymentPayLaterCohortActive &&
    onClearRegistrantBulkPaymentPayLaterCohort
      ? [
          {
            type: "bulkPaymentPayLaterCohort" as const,
            value: "bulk-payment-pay-later-cohort",
            label: "Pay Later (bulk)",
            onRemove: onClearRegistrantBulkPaymentPayLaterCohort,
          },
        ]
      : []),
  ];

  // Clear all filters (top-level and column filters)
  const handleClearAll = () => {
    if (onSelectedJurisdictionsChange) {
      onSelectedJurisdictionsChange([]);
    }
    if (onSelectedLicenseTypesChange) {
      onSelectedLicenseTypesChange([]);
    }
    if (onSelectedRegionsChange) {
      onSelectedRegionsChange([]);
    }
    if (onSelectedRegistrantAccountIdChange) {
      onSelectedRegistrantAccountIdChange(null);
    }
    if (onSelectedAmsChange) {
      onSelectedAmsChange(null);
    }
    if (onColumnFiltersChange) {
      onColumnFiltersChange({});
    }
    if (onClearRegistrantBalanceDueCohort && registrantBalanceDueCohortActive) {
      onClearRegistrantBalanceDueCohort();
    }
    if (
      onClearRegistrantBulkPaymentPayLaterCohort &&
      registrantBulkPaymentPayLaterCohortActive
    ) {
      onClearRegistrantBulkPaymentPayLaterCohort();
    }
  };

  const columnFilterBadges = buildRegistrantColumnFilterBadges(columnFilters);
  const appliedCount = activeFilters.length + columnFilterBadges.length;
  const showActiveBar = appliedCount > 0;

  const removeColumnFilterValue = (
    badge: (typeof columnFilterBadges)[number],
  ) => {
    if (!onColumnFiltersChange) return;
    onColumnFiltersChange(removeColumnFilterBadge(columnFilters, badge));
  };

  return (
    <div className="space-y-4">
      {/* Filter dropdowns */}
      {hasFilterDropdowns ? (
        <div
          className={cn(
            "flex gap-6",
            filterDropdownsLayout === "wrap-row"
              ? "flex-row flex-wrap items-start"
              : "flex-col lg:flex-row",
          )}
        >
          {showJurisdictionFilter &&
            selectedJurisdictions &&
            onSelectedJurisdictionsChange &&
            jurisdictionOptions && (
              <JurisdictionFilter
                selectedJurisdictions={selectedJurisdictions}
                onSelectedJurisdictionsChange={onSelectedJurisdictionsChange}
                jurisdictionOptions={jurisdictionOptions}
              />
            )}

          {showLicenseTypeFilter &&
            selectedLicenseTypes &&
            onSelectedLicenseTypesChange &&
            licenseTypeOptions && (
              <LicenseTypeFilter
                selectedLicenseTypes={selectedLicenseTypes}
                onSelectedLicenseTypesChange={onSelectedLicenseTypesChange}
                licenseTypeOptions={licenseTypeOptions}
              />
            )}

          {hasMultipleRegistrantAccounts &&
            registrantOptions.length > 0 &&
            onSelectedRegistrantAccountIdChange && (
              <RegistrantFilter
                registrantOptions={registrantOptions}
                selectedRegistrantAccountId={selectedRegistrantAccountId}
                onSelectedRegistrantAccountIdChange={
                  onSelectedRegistrantAccountIdChange
                }
              />
            )}

          {amsOptions.length > 0 && onSelectedAmsChange && (
            <AmsFilter
              selectedAms={selectedAms}
              onSelectedAmsChange={onSelectedAmsChange}
              amsOptions={amsOptions}
            />
          )}
        </div>
      ) : null}
      {showJurisdictionFilter && jurisdictionOptionsCriteria ? (
        <p className="text-xs text-muted-foreground">
          {jurisdictionOptionsCriteria}
        </p>
      ) : null}
      {hasMultipleRegistrantAccounts && registrantOptionsCriteria ? (
        <p className="text-xs text-muted-foreground">
          {registrantOptionsCriteria}
        </p>
      ) : null}

      {showActiveBar ? (
        <ActiveFiltersBar
          appliedCount={appliedCount}
          onClearAll={handleClearAll}
          layout={activeFiltersLayout}
        >
          {activeFilters.map((filter) => (
            <RemovableFilterBadge
              key={`${filter.type}-${filter.value}`}
              label={filter.label}
              onRemove={filter.onRemove}
            />
          ))}
          {columnFilterBadges.map((badge) => (
            <RemovableFilterBadge
              key={badge.key}
              label={badge.label}
              onRemove={
                onColumnFiltersChange
                  ? () => removeColumnFilterValue(badge)
                  : undefined
              }
            />
          ))}
        </ActiveFiltersBar>
      ) : null}
    </div>
  );
}
